import axios from "axios";
import crypto from "crypto";
import { getDBFile } from "./fileServices";
import { DollarApollo } from "@vue/apollo-option/types/vue-apollo";
import { ComponentPublicInstance } from "vue";

import DOCUMENTIDS_FROM_SHA256 from "@/graphql/document/GetDocumentIdsFromFileSha256.gql";
import CREATE_DOCUMENT_WITH_FILE_FROM_URL from "@/graphql/document/CreateDocumentWithFileFromUrl.gql";
import { DataForDocument } from "@/types/cutPrevious/dataForDocument.types";
import { FileUrlDoc } from "@/types/cutPrevious/fileUrlDoc.types";

/**
 * Service to upload a file from an url
 * We can have an error from distant server CORS policy
 */
export abstract class UploadUrlDocumentService {

    /**
     * get FileDoc from url
     * Error can be :
     * - error.url : url is not valid
     * - error.response : error in response
     * - error.request : error in request
     * - error.type : mime type is not supported
     * - error.status : status is not 200
     * @param url 
     * @returns FileDoc
     */
    static async getFileFromUrl(url: string): Promise<FileUrlDoc> {

        const file: FileUrlDoc = {
            url: url,
            name: '',
            sha256: '',
            type: '', 
            ext: '',
            size: 0,
            error: '',
            key: ''
        };

        if(!UploadUrlDocumentService.isValidUrl(url)){
            file.error = 'error.url';
            return file;
        }
        
        const response = await axios.get(url,   { responseType: 'blob' }) //browser only
        .catch((error) => {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                file.error = 'error.response';
              } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                file.error = 'error.request';
              } else {
                // Something happened in setting up the request that triggered an Error
                file.error = 'error.request';
              }
        });

        if(response){

            if( response.request.readyState === 4 && response.request.status === 200){

                if(UploadUrlDocumentService.isTypeSupported(response.data.type)){
                    file.dataBlob = response.data;
                    
                    file.type = response.data.type;
                    file.ext = file.type.substring(file.type.lastIndexOf("/") + 1).toUpperCase();
                    file.size = response.data.size;
                    await UploadUrlDocumentService.blobFileSha256(file, response.data);
                }
                else{
                    file.error = 'error.type : ' + response.data.type;
                } 
            }
            else{
                file.error = 'error.status : ' + response.request.status;
            }
        }

        return file;
    }

    /**
     * Check if the type is supported
     * @param type 
     * @returns 
     */
    static isTypeSupported(type: string): boolean {
        let result = false;

        switch (type) {
            case 'image/jpeg':
            case 'image/png':
            case 'image/jpg':
            case 'image/gif':
            case 'application/tif':
            case 'application/tiff':
            case 'application/pdf':
                result = true;
            break;
        }
        return result;
    }

    /**
     * read the blob and calculate the sha256 of content
     * @param file 
     * @param data 
     * @returns 
     */
    static async blobFileSha256(file: FileUrlDoc, data: Blob): Promise<FileUrlDoc> {
        const promise =  new Promise<FileUrlDoc>((resolve, reject) => {

            const readerBinary = new FileReader();
            
            readerBinary.onerror = () => {
                file.error = readerBinary.error.message;
                reject(file);
            };

            readerBinary.onloadend = async () => {

                const dataEncrypted = readerBinary.result
                .toString()
                .replaceAll(/data:.*;base64,/g, "")
                .replace(/\+/g, "-")
                .replace(/\//g, "_");

                // Génère un buffer
                const data = Buffer.from(dataEncrypted.toString(), "base64");
  
                file.sha256 = UploadUrlDocumentService.convertArrayBufferToSha256(data);

                resolve(file);
            };    

            readerBinary.readAsDataURL(data);
        });
        return await Promise.resolve(promise);
    }


    /**
     * 
     * @param arrayBuffer 
     * @returns 
     */
    static convertArrayBufferToSha256(buffer: Buffer): string {
        const decoder = new TextDecoder();

        const datastring = decoder.decode(buffer);
       
        const sha256 = crypto.createHash("sha256").update(datastring).digest("hex");

        return sha256;
    }

    /**
     * Check if the url is valid and secure (https) or not (http)
     * @param url : string of the url
     * @param onlySecurized : boolean to check only https
     * @returns boolean
     */
    static isValidUrl(url: string, onlySecurized: boolean = true): boolean {
        let result = false;

        if(url){
            const regexExpression = onlySecurized ? '^https://' : '^(http|https)://';
            const regex = new RegExp(regexExpression, 'i');
            result = regex.test(url);
        }

        if(!result){
            try {
                new URL(url);
                result = true;
            } catch {
                // console.error('Invalid URL');
            }
        }

        return result;
    }

    /**
     * Check for the access of the url 
     * - CORS
     * @param url 
     * @returns 
     */
    static async canAccessUrl(url: string): Promise<boolean> {
        let result = false;
        try {
            const response = await axios.get(url);
            if(response && response.status === 200){
                result = true;
            }
        } catch (error) {
            console.error('error', error);
        }
        return result;
    }

    /**
     * Create a document file from an url
     * Error can be :
     * - error.createDocument : error in create document
     * 
     * @param url 
     * @param companyId 
     * @param apollo 
     * @param respType 
     * @returns 
     */
    static async createFileFromUrl(fileToCreate: DataForDocument, apollo: DollarApollo<ComponentPublicInstance>): Promise<FileUrlDoc> {
        const file = await UploadUrlDocumentService.getFileFromUrl(fileToCreate.url);
        
        if(!file.error){


            try{
                file.name = fileToCreate.name + '.' + file.ext.toLowerCase();
                await getDBFile(apollo, fileToCreate.companyId, file);

                // The document is not already uploaded and the key is defined
                if (file.status !== "already-uploaded" && file.key){
                   await UploadUrlDocumentService.createDocumentFromFile(file, fileToCreate, apollo);
                }
                else if(file.sha256 && file.type && file.status === "already-uploaded" ){
                    // Search document by sha256 and type
                    await UploadUrlDocumentService.searchDocumentFromSha256(file, fileToCreate, apollo);
                }
                else{
                    file.error = 'error.createDocument';
                }
               
            } catch {
                file.error = 'error.createDocument';
            }
        }
        return file;
    }

    /**
     * Create file from url data
     * Error can be :
     * - error.notCreatedDocument : error when document is not created
     * @param file 
     * @param fileToCreate 
     * @param apollo 
     */
    static async createDocumentFromFile(file: FileUrlDoc, fileToCreate: DataForDocument, apollo: DollarApollo<ComponentPublicInstance>): Promise<void> {
         // Put the file on the server
         await axios.put(file.putAction, file.dataBlob, {
            headers: {
                "Content-Type": file.type,
            },
        });

        const res = await apollo.mutate({
            mutation: CREATE_DOCUMENT_WITH_FILE_FROM_URL
            ,
            variables: {
                name: fileToCreate.name,
                fileKey: file.key,
                fileType: file.ext,
                // url: newFile.url,
                sha256: file.sha256,
                size: file.size,
                companyId: fileToCreate.companyId,
                type: fileToCreate.type,
                status: fileToCreate.status,
                date: fileToCreate.date
            },
        });

        if(res){
            file.documentId = res.data.createDocumentWithFile.id;
        }
        else{
            file.error = 'error.notCreatedDocument';
        }
    }

    /**
     * Search document File from sha256, type, companyId, status, documentType
     * Error can be :
     * - error.alreadyUploaded : error when document is already uploaded and multiple documents are found
     * @param file 
     * @param fileToCreate 
     * @param apollo 
     */
    static async searchDocumentFromSha256(file: FileUrlDoc, fileToCreate: DataForDocument, apollo: DollarApollo<ComponentPublicInstance>): Promise<void> {
        const documentRes = await apollo.query({
            query: DOCUMENTIDS_FROM_SHA256,
            variables: {
                sha256: file.sha256,
                type: file.ext,
                documentType: fileToCreate.type,
                status: fileToCreate.status,
                companyId: fileToCreate.companyId,
                companyId2: fileToCreate.companyId,
            },
        });

        if(documentRes && documentRes.data.documents.length === 1){
            file.documentId = documentRes.data.documents[0].id;
        }
        else{
            file.error = 'error.alreadyUploaded';
        }
    }
}

